/**
 * Builds a human-readable request id for new event submissions.
 *
 * Canonical shape:
 *
 *   REQ<YY><DDD>-<TS5><R3>
 *
 *   YY   = last two digits of the UTC year, zero-padded   (e.g. 2026 → "26")
 *   DDD  = UTC day-of-year, zero-padded to 3 digits        (e.g. 5 Jun → "156")
 *   TS5  = the last 5 base36 characters of Date.now()      (encodes the exact
 *          millisecond-precision timestamp; the low-order
 *          window rolls over roughly every ~16 hours,
 *          giving high collision resistance within a day)
 *   R3   = 3 cryptographically random uppercase-base36 chars
 *
 * Example: `REQ26156-8B7C2X9Q`
 *
 * The id is generated exactly once per event-create flow. Lifecycle stability
 * (rename / save / patch / submit / cancel must reuse the id) is enforced by
 * the caller, not by this generator.
 *
 * The backend envelope regex `^REQ-?[A-Za-z0-9][A-Za-z0-9\-]*$` accepts this
 * shape; legacy UUID-style ids continue to validate forever.
 */

const TIMESTAMP_SEGMENT_LENGTH = 5;
const RANDOM_SEGMENT_LENGTH = 3;
const BASE36_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

export interface BuildRequestIdInput {
  /** Optional injection point for deterministic tests. */
  now?: Date;
  /**
   * Optional injection point for deterministic tests. Must return at least
   * {@link RANDOM_SEGMENT_LENGTH} bytes.
   */
  randomBytes?: () => Uint8Array;
}

/** Formats the date segment `<YY><DDD>` from a UTC `Date`. */
export function makeDateSegment(now: Date): string {
  const yy = String(now.getUTCFullYear() % 100).padStart(2, '0');
  const startOfYearUtc = Date.UTC(now.getUTCFullYear(), 0, 1);
  const ms = now.getTime() - startOfYearUtc;
  const dayOfYear = Math.floor(ms / 86_400_000) + 1;
  const ddd = String(dayOfYear).padStart(3, '0');
  return `${yy}${ddd}`;
}

/**
 * Encodes a millisecond-precision timestamp as 5 uppercase-base36 chars
 * (low-order window). Pads with leading zeros if the encoding is shorter
 * (only possible for timestamps from before ~1970-01-17, i.e. never in
 * practice).
 */
export function makeTimestampSegment(epochMs: number): string {
  const encoded = Math.floor(epochMs).toString(36).toUpperCase();
  return encoded.slice(-TIMESTAMP_SEGMENT_LENGTH).padStart(TIMESTAMP_SEGMENT_LENGTH, '0');
}

function defaultRandomBytes(): Uint8Array {
  const buf = new Uint8Array(RANDOM_SEGMENT_LENGTH);
  globalThis.crypto.getRandomValues(buf);
  return buf;
}

/** Maps a random byte buffer to a 3-char uppercase-base36 string. */
export function makeRandomSegment(randomBytes: () => Uint8Array = defaultRandomBytes): string {
  const buf = randomBytes();
  let out = '';
  for (let i = 0; i < RANDOM_SEGMENT_LENGTH; i++) {
    const byte = buf[i] ?? 0;
    out += BASE36_ALPHABET[byte % BASE36_ALPHABET.length];
  }
  return out;
}

/**
 * Builds a request id of the form `REQ<YY><DDD>-<TS5><R3>`.
 *
 * @param input Optional injection points (`now`, `randomBytes`) for tests. In
 * production all callers should invoke `buildRequestId()` with no arguments.
 */
export function buildRequestId(input: BuildRequestIdInput = {}): string {
  const now = input.now ?? new Date();
  const dateSegment = makeDateSegment(now);
  const timestampSegment = makeTimestampSegment(now.getTime());
  const randomSegment = makeRandomSegment(input.randomBytes);
  return `REQ${dateSegment}-${timestampSegment}${randomSegment}`;
}
